/**
 * Lógica pura de valoración de inventario (CC-6: NUNCA usar macros lingui aquí).
 *
 * Método AVCO (Average Cost / costo promedio móvil): cada entrada (recepción /
 * compra) recalcula el costo unitario promedio del producto; cada salida se
 * valora al costo promedio vigente. Las capas de valoración (valuation_layer)
 * son append-only y auditables: una por movimiento con impacto de valor.
 *
 *   - Entrada (qty > 0): unit_cost = costo de la compra; value = qty × unit_cost.
 *                        El promedio móvil se recalcula sobre el saldo acumulado.
 *   - Salida  (qty < 0): unit_cost = promedio móvil vigente; value = qty × avg
 *                        (negativo, descuenta valor del inventario).
 *
 * TODO en ENTEROS (unidades menores, factor 1000), igual que F1/F2:
 *   - Las CANTIDADES están escaladas ×1000 (QTY_SCALE).
 *   - Los COSTOS UNITARIOS están en unidades menores de dinero (p. ej. céntimos)
 *     pero expresados POR UNIDAD ENTERA de producto (no por milésima).
 *   - value = round(qty_minor × unit_cost / QTY_SCALE) → unidades menores de dinero.
 *
 * Esto evita arrastrar el factor 1000 dentro del costo unitario y mantiene
 * `value` en la misma escala monetaria que `unit_cost`.
 */

import { QTY_SCALE } from "./units";

/** Estado acumulado de valoración de un producto (saldo de cantidad y valor). */
export interface AvcoState {
  /** Cantidad on-hand acumulada (unidades menores, ×1000). */
  qty: number;
  /** Valor total acumulado del inventario (unidades menores de dinero). */
  value: number;
}

/** Estado inicial vacío. */
export const EMPTY_AVCO_STATE: AvcoState = { qty: 0, value: 0 };

/**
 * Calcula el valor (unidades menores de dinero) de una cantidad dada a un costo
 * unitario por unidad entera. `qtyMinor` está escalado ×1000.
 */
export function valueOf(qtyMinor: number, unitCost: number): number {
  return Math.round((qtyMinor * unitCost) / QTY_SCALE);
}

/**
 * Costo promedio móvil vigente (por unidad entera) dado el estado acumulado.
 * Devuelve 0 si no hay saldo (evita división por cero).
 */
export function averageUnitCost(state: AvcoState): number {
  if (state.qty <= 0) {
    return 0;
  }
  return Math.round((state.value * QTY_SCALE) / state.qty);
}

/** Resultado de aplicar un movimiento de valoración. */
export interface AvcoLayer {
  /** Delta de cantidad (unidades menores, ×1000). >0 entrada, <0 salida. */
  qty: number;
  /** Costo unitario (por unidad entera) imputado a esta capa. */
  unitCost: number;
  /** Valor de la capa (unidades menores de dinero). Signo sigue a qty. */
  value: number;
  /** Estado acumulado tras aplicar la capa. */
  state: AvcoState;
}

/**
 * Aplica una ENTRADA (recepción/compra) al estado AVCO. `qtyMinor` > 0 (×1000),
 * `purchaseUnitCost` es el costo de compra por unidad entera. Recalcula el
 * promedio móvil sumando cantidad y valor.
 */
export function applyInbound(
  state: AvcoState,
  qtyMinor: number,
  purchaseUnitCost: number,
): AvcoLayer {
  const layerValue = valueOf(qtyMinor, purchaseUnitCost);
  const next: AvcoState = {
    qty: state.qty + qtyMinor,
    value: state.value + layerValue,
  };
  return { qty: qtyMinor, unitCost: purchaseUnitCost, value: layerValue, state: next };
}

/**
 * Aplica una SALIDA (entrega/consumo) al estado AVCO. `qtyMinor` > 0 (×1000): la
 * capa resultante es negativa. La salida se valora al promedio móvil VIGENTE
 * (antes de descontar). Devuelve qty/value negativos.
 */
export function applyOutbound(state: AvcoState, qtyMinor: number): AvcoLayer {
  const avg = averageUnitCost(state);
  const layerValue = valueOf(qtyMinor, avg);
  const next: AvcoState = {
    qty: state.qty - qtyMinor,
    value: state.value - layerValue,
  };
  // `|| 0` evita el -0 cuando layerValue/qtyMinor son 0 (Object.is(-0,0) === false).
  return { qty: -qtyMinor || 0, unitCost: avg, value: -layerValue || 0, state: next };
}

/** Una capa de valoración ya persistida (subset relevante para reducir). */
export interface ValuationLayerLike {
  product_id: string | null;
  qty: number | null;
  value: number | null;
}

/**
 * Reduce las capas de un producto a su estado AVCO acumulado (saldo de cantidad
 * y valor). Las capas deben venir en orden cronológico de creación.
 */
export function reduceLayers(layers: ValuationLayerLike[]): AvcoState {
  let qty = 0;
  let value = 0;
  for (const layer of layers) {
    qty += layer.qty ?? 0;
    value += layer.value ?? 0;
  }
  return { qty, value };
}

/** Valor de inventario on-hand por producto, derivado de las capas. */
export interface InventoryValueRow {
  productId: string;
  /** On-hand acumulado (unidades menores, ×1000). */
  onHand: number;
  /** Valor total del inventario (unidades menores de dinero). */
  value: number;
  /** Costo promedio móvil vigente (por unidad entera). */
  avgUnitCost: number;
}

/**
 * Agrega las capas de valoración por producto y devuelve el valor de inventario
 * on-hand de cada uno (on-hand, valor total y costo promedio). Reporte simple de
 * valoración (CC-6: cálculo puro, sin lingui).
 */
export function computeInventoryValue(layers: ValuationLayerLike[]): InventoryValueRow[] {
  const byProduct = new Map<string, AvcoState>();
  for (const layer of layers) {
    const pid = layer.product_id;
    if (!pid) {
      continue;
    }
    const cur = byProduct.get(pid) ?? { qty: 0, value: 0 };
    cur.qty += layer.qty ?? 0;
    cur.value += layer.value ?? 0;
    byProduct.set(pid, cur);
  }
  const rows: InventoryValueRow[] = [];
  for (const [productId, state] of byProduct) {
    rows.push({
      productId,
      onHand: state.qty,
      value: state.value,
      avgUnitCost: averageUnitCost(state),
    });
  }
  return rows;
}
